import api from 'services/api';
import { array, bool, enforce, nullable, num, object, runSchema, str } from 'validation';

import { folderPathSchema, type FolderPath } from 'api/shared/folderPath';
import { recurrenceSchema, type Recurrence, type SaveRecurrenceDefinition } from './types';

// PUT /recurrence_definitions/:zone_name/:name — update. Backend:
// `RecurrenceDefinitionService.update` returns the `UPDATE … RETURNING` row with
// `zone_name` echoed from the resolved new zone + the shared `recurrences` /
// `folder_path` serializers.
//
// `UpdatedRecurrenceDefinition` is its OWN type + schema for the same reason as
// `CreatedRecurrenceDefinition` — a distinct backend statement that happens to
// return the same shape. Structural typing lets it flow wherever a
// `RecurrenceDefinition` is expected downstream.
export interface UpdatedRecurrenceDefinition {
  readonly recurrence_definition_id: number;
  readonly name: string;
  readonly description: string | null;
  readonly zone_name: string | null;
  readonly resubmit_on_error: boolean;
  readonly recurrences: readonly Recurrence[];
  readonly folder_id: number | null;
  readonly folder_path: FolderPath | null;
  readonly created_at: string;
  readonly updated_at: string;
}

const updatedSchema = object<UpdatedRecurrenceDefinition>({
  recurrence_definition_id: num(),
  name: str(),
  description: nullable(str()),
  zone_name: nullable(str()),
  resubmit_on_error: bool(),
  recurrences: array(recurrenceSchema),
  folder_id: nullable(num()),
  folder_path: nullable(folderPathSchema),
  created_at: str(),
  updated_at: str(),
});

/**
 * PUT `/recurrence_definitions/:zone_name/:name`. The URL params identify the
 * *original* record (pre-edit); the body carries the new values, which may
 * include changes to `name` and `zone_name`. Returns the updated record.
 */
export const putRecurrenceDefinition = async (
  original: { zone_name: string; name: string },
  save: SaveRecurrenceDefinition
): Promise<UpdatedRecurrenceDefinition> => {
  const res = await api.put(`/recurrence_definitions/${encodeURIComponent(original.zone_name)}/${encodeURIComponent(original.name)}`, save);
  const r = runSchema(updatedSchema, res.data, 'putRecurrenceDefinition');
  if (!r.ok) {
    throw new Error('putRecurrenceDefinition: response failed validation');
  }
  return enforce(r.value, r.warnings, ['recurrence_definition_id']);
};
